import SEOHead from '../components/SEOHead';

const CONTACT_EMAIL = 'optionview580@gmail.com';
const LAST_UPDATED = 'March 4, 2026';

const H2 = 'text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2';
const LINK = 'text-primary-600 dark:text-primary-400 hover:underline';

export default function TermsOfService() {
  return (
    <>
      <SEOHead
        title="Terms of Service"
        description="Terms of Service for OptionView — the free IV calculator for Indian options markets. Read our terms before using the service."
        canonicalPath="/terms-of-service"
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">Terms of Service</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-8">Last updated: {LAST_UPDATED}</p>

        <div className="bg-surface border border-surface rounded-clay shadow-clay dark:shadow-clay-dark backdrop-blur-sm p-6 sm:p-8 space-y-6 text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">

          <section>
            <h2 className={H2}>1. Acceptance of Terms</h2>
            <p>
              By accessing or using optionview.in (the "Service"), you agree to be bound by
              these Terms of Service ("Terms"). If you disagree with any part of these Terms,
              please do not use the Service. These Terms apply to all visitors, users, and
              others who access or use the Service.
            </p>
          </section>

          <section>
            <h2 className={H2}>2. Use of the Service</h2>
            <p className="mb-2">OptionView is provided free of charge for personal, non-commercial use. By using the Service, you represent and warrant that:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>You are at least 18 years of age, or have obtained verifiable parental consent</li>
              <li>You will use the Service only for lawful purposes</li>
              <li>You will comply with all applicable local, state, national, and international laws and regulations</li>
              <li>Your use of the Service will not violate any third-party rights</li>
            </ul>
          </section>

          <section>
            <h2 className={H2}>3. Prohibited Activities</h2>
            <p className="mb-2">You agree not to engage in any of the following activities:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Scraping, crawling, or automated extraction of data from the Service without our express written permission</li>
              <li>Reverse engineering, decompiling, or disassembling any part of the Service</li>
              <li>Using the Service to transmit harmful, offensive, or illegal content</li>
              <li>Attempting to gain unauthorised access to any part of the Service or its servers</li>
              <li>Interfering with or disrupting the integrity or performance of the Service</li>
              <li>Reproducing, distributing, or commercially exploiting any content from the Service without permission</li>
              <li>Using the Service for any fraudulent or deceptive purpose</li>
            </ul>
          </section>

          <section>
            <h2 className={H2}>4. Intellectual Property</h2>
            <p className="mb-2">
              All content on optionview.in — including but not limited to text, graphics, code,
              design, data visualisations, and the IV calculation methodology — is the property
              of Option View and is protected by applicable intellectual property laws. You may
              not copy, modify, distribute, sell, or lease any part of the Service or its
              content without our prior written consent.
            </p>
            <p>
              Market data displayed on the Service is sourced from multiple different, freely available
              sources. This data is the provider's intellectual property.
            </p>
          </section>

          <section>
            <h2 className={H2}>5. Disclaimer of Warranties</h2>
            <p className="mb-2">
              The Service is provided on an <strong>"AS IS"</strong> and{' '}
              <strong>"AS AVAILABLE"</strong> basis, without warranties of any kind, either
              express or implied. We specifically disclaim:
            </p>
            <ul className="list-disc pl-5 space-y-1 mb-2">
              <li>Any warranty that the Service will be uninterrupted, error-free, or secure</li>
              <li>Any warranty regarding the accuracy, completeness, or timeliness of market data</li>
              <li>Any implied warranties of merchantability, fitness for a particular purpose, or non-infringement</li>
            </ul>
            <p>
              The IV calculations on this Service are based on publicly available end-of-day
              data and mathematical models (Black-Scholes). They are provided for informational
              purposes only and should not be used as the sole basis for any trading decision.
            </p>
          </section>

          <section>
            <h2 className={H2}>6. Limitation of Liability</h2>
            <p className="mb-2">
              To the fullest extent permitted by applicable law, Option View shall not be liable
              for any indirect, incidental, special, consequential, or punitive damages, including
              but not limited to:
            </p>
            <ul className="list-disc pl-5 space-y-1 mb-2">
              <li>Trading losses or financial losses of any kind arising from use of the Service</li>
              <li>Losses due to inaccurate, delayed, or incomplete market data</li>
              <li>Losses due to service interruptions, downtime, or technical errors</li>
              <li>Any loss of data, revenue, or goodwill</li>
            </ul>
            <p>
              As the Service is provided free of charge, our maximum aggregate liability to you
              for any claim arising from use of the Service is <strong>₹0 (zero rupees)</strong>.
            </p>
          </section>

          <section>
            <h2 className={H2}>7. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless Option View and its operators
              from and against any claims, damages, costs, or liabilities arising out of or
              relating to your use of the Service, your violation of these Terms, or your
              violation of any third-party rights.
            </p>
          </section>

          <section>
            <h2 className={H2}>8. Third-Party Links</h2>
            <p>
              The Service may contain links to third-party websites (such as NSE or SEBI). These
              links are provided for convenience only. We have no control over the content of
              those sites and accept no responsibility for them or for any loss or damage that
              may arise from your use of them.
            </p>
          </section>

          <section>
            <h2 className={H2}>9. Termination</h2>
            <p>
              We reserve the right to terminate or suspend access to the Service at any time,
              without prior notice or liability, for any reason, including if you breach these
              Terms. Upon termination, your right to use the Service will cease immediately.
              You may stop using the Service at any time.
            </p>
          </section>

          <section>
            <h2 className={H2}>10. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of
              India, without regard to its conflict of law provisions. Any disputes arising
              out of or in connection with these Terms shall be subject to the exclusive
              jurisdiction of the courts of India. You consent to the personal jurisdiction
              of such courts and waive any objection to the exercise of jurisdiction over you.
            </p>
          </section>

          <section>
            <h2 className={H2}>11. Changes to These Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Changes will be posted
              on this page with an updated "Last updated" date. Continued use of the Service
              after any changes constitutes your acceptance of the new Terms. It is your
              responsibility to review these Terms periodically.
            </p>
          </section>

          <section>
            <h2 className={H2}>12. Contact Us</h2>
            <p>
              If you have questions about these Terms of Service, please contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className={LINK}>
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </section>

        </div>
      </div>
    </>
  );
}
